import React from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import Iconify from 'src/components/Iconify';
import MetricCard from './components/MetricCard';
import { MKT_ANALYTICS_DESIGN as d } from './MktAnalytics.design';

const PRIMARY = '#FBBC04';
const SECONDARY = '#34A853';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const dt = new Date(dateStr);
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}`;
}

function formatNumber(num) {
  if (num == null) return '—';
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toLocaleString('pt-BR');
}

function formatCurrency(num) {
  if (num == null) return '—';
  return `R$ ${Number(num).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPercent(num) {
  if (num == null) return '—';
  return `${Number(num).toFixed(2)}%`;
}

const STATUS_STYLES = {
  ENABLED: 'bg-green-50 text-green-700 border-green-200',
  PAUSED: 'bg-amber-50 text-amber-700 border-amber-200',
  REMOVED: 'bg-red-50 text-red-700 border-red-200',
};

const STATUS_DOT = {
  ENABLED: 'bg-green-500',
  PAUSED: 'bg-amber-500',
  REMOVED: 'bg-red-500',
};

const TYPE_STYLES = {
  SEARCH: 'bg-blue-50 text-blue-600 border-blue-200',
  DISPLAY: 'bg-purple-50 text-purple-600 border-purple-200',
  VIDEO: 'bg-red-50 text-red-600 border-red-200',
  SHOPPING: 'bg-green-50 text-green-600 border-green-200',
  PERFORMANCE_MAX: 'bg-amber-50 text-amber-600 border-amber-200',
};

export default function GoogleAdsTab({ data }) {
  if (!data) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-amber-200 border-t-[#FBBC04] rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Carregando dados do Google Ads...</p>
        </div>
      </div>
    );
  }

  const { summary, daily, campaigns } = data;

  const dailyFormatted = (daily || []).map((row) => ({
    ...row,
    dateLabel: formatDate(row.date),
  }));

  const funnelSteps = [
    { label: 'Impressions', value: summary?.impressions || 0, icon: 'eva:eye-outline', color: '#FBBC04' },
    { label: 'Clicks', value: summary?.clicks || 0, icon: 'eva:cursor-outline', color: '#EA4335' },
    { label: 'Conversions', value: summary?.conversions || 0, icon: 'eva:checkmark-circle-outline', color: '#34A853' },
  ];

  const maxFunnel = funnelSteps[0].value || 1;

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <MetricCard
          icon="eva:eye-outline"
          label="Impressions"
          value={formatNumber(summary?.impressions)}
          growth={summary?.impressions_growth_pct}
          color={PRIMARY}
        />
        <MetricCard
          icon="eva:diagonal-arrow-right-up-outline"
          label="Clicks"
          value={formatNumber(summary?.clicks)}
          growth={summary?.clicks_growth_pct}
          color={PRIMARY}
        />
        <MetricCard
          icon="eva:percent-outline"
          label="CTR"
          value={formatPercent(summary?.ctr)}
          color={PRIMARY}
        />
        <MetricCard
          icon="eva:pricetags-outline"
          label="CPC"
          value={formatCurrency(summary?.cpc)}
          color={PRIMARY}
        />
        <MetricCard
          icon="eva:credit-card-outline"
          label="Investimento"
          value={formatCurrency(summary?.cost)}
          growth={summary?.cost_growth_pct}
          color="#EA4335"
        />
        <MetricCard
          icon="eva:checkmark-circle-outline"
          label="Conversions"
          value={formatNumber(summary?.conversions)}
          growth={summary?.conversions_growth_pct}
          color={SECONDARY}
        />
        <MetricCard
          icon="eva:trending-up-outline"
          label="ROAS"
          value={summary?.roas != null ? `${Number(summary.roas).toFixed(2)}x` : '—'}
          color={SECONDARY}
        />
      </div>

      {/* Spend vs Conversions (Dual Y Axis) */}
      <div className={d.charts.container}>
        <h3 className={d.charts.title}>
          <Iconify icon="eva:trending-up-outline" width={16} className="inline mr-1.5 text-[#FBBC04]" />
          Investimento vs Conversions
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={dailyFormatted}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
            <YAxis
              yAxisId="cost"
              orientation="left"
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => `R$${v}`}
            />
            <YAxis
              yAxisId="conversions"
              orientation="right"
              tick={{ fontSize: 11 }}
            />
            <Tooltip
              formatter={(value, name) => {
                if (name === 'Investimento') return formatCurrency(value);
                return formatNumber(value);
              }}
            />
            <Legend />
            <Line
              yAxisId="cost"
              type="monotone"
              dataKey="cost"
              name="Investimento"
              stroke="#EA4335"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Line
              yAxisId="conversions"
              type="monotone"
              dataKey="conversions"
              name="Conversions"
              stroke={SECONDARY}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Clicks & Impressions Bar Chart */}
      <div className={d.charts.container}>
        <h3 className={d.charts.title}>
          <Iconify icon="eva:bar-chart-outline" width={16} className="inline mr-1.5 text-[#FBBC04]" />
          Clicks e Impressions por Dia
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={dailyFormatted}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="impressions" orientation="left" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="clicks" orientation="right" tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Bar yAxisId="impressions" dataKey="impressions" name="Impressions" fill={PRIMARY} radius={[3, 3, 0, 0]} opacity={0.7} />
            <Bar yAxisId="clicks" dataKey="clicks" name="Clicks" fill={SECONDARY} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Funnel Visualization */}
      <div className={d.charts.container}>
        <h3 className={d.charts.title}>
          <Iconify icon="eva:funnel-outline" width={16} className="inline mr-1.5 text-[#FBBC04]" />
          Funil de Conversão
        </h3>
        <div className="flex flex-col items-center gap-3 py-4">
          {funnelSteps.map((step, idx) => {
            const widthPct = Math.max(((step.value / maxFunnel) * 100), 15);
            return (
              <div key={step.label} className="w-full flex flex-col items-center">
                <div
                  className="rounded-xl py-4 px-6 flex items-center justify-between transition-all"
                  style={{
                    width: `${widthPct}%`,
                    backgroundColor: `${step.color}18`,
                    border: `1px solid ${step.color}40`,
                    minWidth: '200px',
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${step.color}25` }}
                    >
                      <Iconify icon={step.icon} width={18} style={{ color: step.color }} />
                    </div>
                    <span className="text-sm font-semibold text-gray-800">{step.label}</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">{formatNumber(step.value)}</span>
                </div>
                {idx < funnelSteps.length - 1 && (
                  <div className="flex flex-col items-center my-1">
                    <Iconify icon="eva:arrow-down-outline" width={20} className="text-gray-300" />
                    {idx === 0 && summary?.ctr != null && (
                      <span className="text-xs text-gray-400 font-medium">CTR: {formatPercent(summary.ctr)}</span>
                    )}
                    {idx === 1 && summary?.conversion_rate != null && (
                      <span className="text-xs text-gray-400 font-medium">Conv. Rate: {formatPercent(summary.conversion_rate)}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Campaign Performance Table */}
      <div className={d.table.container}>
        <div className={d.table.header}>
          <h3 className={d.table.headerTitle}>
            <Iconify icon="eva:briefcase-outline" width={16} className="inline mr-1.5 text-[#FBBC04]" />
            Performance por Campanha
          </h3>
          <span className={d.table.cellMuted}>
            {summary?.active_campaigns || 0} campanhas ativas
          </span>
        </div>

        {/* Table Header */}
        <div className="px-5 py-2.5 border-b border-gray-100 flex items-center bg-gray-50/60 gap-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex-1 min-w-0">Campanha</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-16 text-center">Tipo</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-20 text-right">Impressions</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-16 text-right">Clicks</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-14 text-right">CTR</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-16 text-right">CPC</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-20 text-right">Custo</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-16 text-right">Conv.</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-20 text-center">Status</span>
        </div>

        {/* Table Rows */}
        <div className="max-h-[450px] overflow-y-auto">
          {(campaigns || []).map((campaign, idx) => (
            <div key={campaign.id || idx} className={`${d.table.row} gap-2`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{campaign.name || '—'}</p>
              </div>
              <span className="w-16 flex justify-center">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase border ${TYPE_STYLES[campaign.type] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                  {campaign.type || '—'}
                </span>
              </span>
              <span className="text-sm text-gray-700 w-20 text-right">{formatNumber(campaign.impressions)}</span>
              <span className="text-sm text-gray-700 w-16 text-right">{formatNumber(campaign.clicks)}</span>
              <span className="text-sm text-gray-700 w-14 text-right">{formatPercent(campaign.ctr)}</span>
              <span className="text-sm text-gray-700 w-16 text-right">{formatCurrency(campaign.cpc)}</span>
              <span className="text-sm font-medium text-gray-900 w-20 text-right">{formatCurrency(campaign.cost)}</span>
              <span className="text-sm text-gray-700 w-16 text-right">{formatNumber(campaign.conversions)}</span>
              <span className="w-20 flex justify-center">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${STATUS_STYLES[campaign.status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[campaign.status] || 'bg-gray-400'}`} />
                  {campaign.status || '—'}
                </span>
              </span>
            </div>
          ))}
          {(!campaigns || campaigns.length === 0) && (
            <div className="px-5 py-12 text-center">
              <Iconify icon="eva:briefcase-outline" width={32} className="text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Nenhuma campanha encontrada</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
